import { AxiosError } from "axios";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { NetworkResponse } from "~/app/type/common";
import { NOTIFICATIONS } from "~/constans/notifications";
import { STATUS_TYPES } from "~/constans/statuses";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";

interface IRequestWithNotifHandlerProps<RequestReturn> {
  request: () => NetworkResponse<RequestReturn>;
  callback?: (res: RequestReturn) => void;
  errorPrefixMessage?: string;
}

interface IErrorHandlerFuncParams {
  e: unknown;
  addNotificationMutation;
  t: TFunction;
  errorPrefixMessage?: string;
  duration?: number;
}

// Достаём осмысленное сообщение об ошибке из ответа бэка.
// Бэк отдаёт message в трёх форматах:
//   - строка: { message: "У постоянного РМ ... нет общих направлений ..." }
//   - массив строк: { message: ["err1", "err2"] } (nest validation)
//   - объект-обёртка: { message: { message: "Validation failed", errors: [...] } }
//     либо { message: { message: "...", results: { errors: [...] } } }
//     (частичные ошибки PUT /user/temporary-assignments — детали важны,
//     поэтому склеиваем их с основным сообщением).
// В любом другом случае возвращаем undefined и fallback на e.message.
function extractBackendMessage(e: AxiosError): string | undefined {
  const msg = (e.response?.data as { message?: unknown } | undefined)?.message;
  if (typeof msg === "string" && msg.length > 0) return msg;
  if (Array.isArray(msg) && typeof msg[0] === "string") return msg.join("; ");
  if (msg && typeof msg === "object") {
    const inner = (msg as { message?: unknown }).message;
    const details =
      (msg as { errors?: unknown; results?: { errors?: unknown } }).results
        ?.errors ?? (msg as { errors?: unknown }).errors;
    // Детали (список ошибок по дилерам и т.п.) выводим маркированным списком
    // построчно — Notification рендерит content с white-space: pre-line.
    const detailText =
      Array.isArray(details) && details.every((d) => typeof d === "string")
        ? (details as string[])
            .filter(Boolean)
            .map((d) => `• ${d}`)
            .join("\n")
        : "";
    if (typeof inner === "string" && inner.length > 0) {
      return detailText ? `${inner}:\n${detailText}` : inner;
    }
    if (detailText) return detailText;
  }
  return undefined;
}

export const defaultErrorHandlerFunc = (params: IErrorHandlerFuncParams) => {
  const { e, addNotificationMutation, t, errorPrefixMessage, duration } =
    params;

  if (e instanceof AxiosError) {
    // Если бэк вернул осмысленный message — показываем его. Иначе — e.message
    // («Request failed with status code 400» — технический, но хоть что-то).
    // errorPrefixMessage клеится спереди через ": " в любом случае.
    const message = extractBackendMessage(e) ?? e.message;
    addNotificationMutation({
      variables: {
        status: STATUS_TYPES.ERROR,
        type: NOTIFICATIONS.error,
        content: `${errorPrefixMessage ? errorPrefixMessage + ": " : ""}${message}`,
        duration,
      },
    });
    return e;
  }
  addNotificationMutation({
    variables: {
      status: STATUS_TYPES.ERROR,
      type: NOTIFICATIONS.error,
      content: t("errorMessages.default"),
      duration,
    },
  });
};

const useRequestHandler = () => {
  const { t } = useTranslation();
  const [addNotificationMutation] = useAddNotificationMutation({});

  const requestWithNotifHandler = async <RequestCallback>(
    params: IRequestWithNotifHandlerProps<RequestCallback>,
  ) => {
    try {
      const res = await params.request();
      if (res && res?.status >= 200 && res?.status < 300 && params.callback) {
        params?.callback?.(res.data);
      }
      return res;
    } catch (e) {
      defaultErrorHandlerFunc({
        e,
        t,
        addNotificationMutation,
        errorPrefixMessage: params.errorPrefixMessage,
      });
    }
  };

  return {
    requestWithNotifHandler,
  };
};

export default useRequestHandler;
